"use client";

import { useState } from "react";
import { AuthLayout } from "@/components/ui/auth-layout";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, Label } from "@/components/ui/fieldset";
import { Heading } from "@/components/ui/heading";
import { Input } from "@/components/ui/input";
import { Text, TextLink } from "@/components/ui/text";
import { ApiError, api } from "@/lib/api";

export default function ForgotPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/v1/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 202) {
        setDone(true);
      } else {
        setError(err instanceof Error ? err.message : "Unexpected error");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm">
        <Heading>Reset password</Heading>
        <Text className="mt-1">
          We&apos;ll email you a link if an account exists for that address.
        </Text>

        {done ? (
          <div className="mt-6 flex flex-col gap-3">
            <p className="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200">
              If that email is registered, a reset link is on its way.
            </p>
            <TextLink href="/login" className="text-sm">
              Back to login
            </TextLink>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6">
            <FieldGroup>
              <Field>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                />
              </Field>
            </FieldGroup>
            <div className="mt-6 flex flex-col gap-2">
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? "Sending…" : "Send reset link"}
              </Button>
              <TextLink href="/login" className="text-center text-xs">
                Back to login
              </TextLink>
            </div>
            {error && (
              <p className="mt-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
                {error}
              </p>
            )}
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
